import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column, ExpandedColumn } from '@repo/ui/data-table'
import { TableBoolean, TableDate, TableLink } from '@repo/ui/data-table-cells'
import FacesEvaluation from '@repo/ui/faces-evaluation'
import {
  BellIcon,
  EmailIcon,
  EyeOpenIcon,
  LevelIcon,
  ListApprovedIcon,
  NotebookIcon,
  PlusIcon,
  UserCanceledIcon,
  UserCheckIcon,
  UserIcon,
  XIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getNationalitiesList } from '../../api/countries/get-nationalities-list'
import { assignToPcr } from '../../api/staff-application/assign-to-pcr'
import { changeStaffApplicationEvaluation } from '../../api/staff-application/change-application-evaluation'
import { changeStaffApplicationPriority } from '../../api/staff-application/change-application-priority'
import { changeStaffApplicationStatus } from '../../api/staff-application/change-application-status'
import { getStaffApplicationCurrentScoreCard } from '../../api/staff-application/get-staff-application-current-score-card'
import { rejectStaffApplication } from '../../api/staff-application/reject-application'
import { sendEmailToStaffs } from '../../api/staff-application/send-email-to-staffs'
import { sendReminderToPcr } from '../../api/staff-application/send-reminder-to-pcr'
import { validateStaffApplications } from '../../api/staff-application/validate-application'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import AssignPcrForm from '../../components/pages/candidates/assign-pcr-form'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import StaffApplicationPriorityForm from '../../components/pages/candidates/staff-application-priority-form'
import StaffApplicationStatusForm from '../../components/pages/candidates/staff-application-status-form'
import StaffApplicationSummary from '../../components/pages/candidates/staff-application-summary'
import StaffApplicationsFilter from '../../components/pages/candidates/staff-applications-filter'
import { PcrScoreCardModal } from '../../components/pages/pcr/pcr-score-card-form/pcr-score-card-form'
import { mapScoreCardToInput } from '../../components/pages/staff-application/utils'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { GenderEnum } from '../../lib/types/enums/common/gender.enum'
import { languageEnumMapping } from '../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../lib/types/enums/languages/language-level.enum'
import {
  EvalutationEnum,
  mapEvaluationFace,
  mapFaceEvaluation
} from '../../lib/types/enums/staff-application/evalutation.enum'
import {
  PriorityEnum,
  priorityEnumMapping
} from '../../lib/types/enums/staff-application/priority.enum'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../lib/types/enums/staff-application/staff-application-status.enum'
import { staffApplicationTypeMapping } from '../../lib/types/enums/staff-application/staff-application-type.enum'
import { pcrResult } from '../../lib/types/models/score-card/score-card'
import {
  StaffApplication,
  StaffApplicationFilters
} from '../../lib/types/models/staff-application/staff-application'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformStaffApplicationFiltersToUrl,
  transformUrlToStaffApplicationFilters
} from '../../lib/utils/filters-mapping/staff-applications'
import { getNationalityLabel } from '../../lib/utils/functions/country.functions'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export const applicationsInitialFilter: StaffApplicationFilters = {
  periods: [],
  diplomas: [],
  languages: [],
  statuses: [
    StaffApplicationStatusEnum.TO_BE_CONTACTED,
    StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED,
    StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED,
    StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED,
    StaffApplicationStatusEnum.INTERVIEW_SCHEDULED,
    StaffApplicationStatusEnum.INTERVIEW_DONE,
    StaffApplicationStatusEnum.TO_BE_ASSIGNED_TO_PCR
  ],
  types: [],
  evaluations: [EvalutationEnum.GOOD, EvalutationEnum.MIDDLE],
  similarApplications: true
}

// "Réinitialiser" clears every filter, including the defaults above
const applicationsEmptyFilter: StaffApplicationFilters = {
  periods: [],
  diplomas: [],
  languages: [],
  statuses: [],
  types: [],
  evaluations: [],
  similarApplications: true
}

const StaffApplicationsList = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const tableRef = React.useRef<DynamicTableRef<StaffApplication>>(null)

  const [isTableLoading, setIsTableLoading] = useState(false)
  const languageMapping = languageEnumMapping()

  const { selectedFilter, filter, onFilterChange, onSelectedFilterChange } =
    useFilters<StaffApplicationFilters>({
      initialFilters: applicationsInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffApplicationFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffApplicationFilters
    })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'assign-pcr' | 'reject-application' | 'filters' | 'send-email',
    StaffApplication[]
  >(['assign-pcr', 'reject-application', 'filters', 'send-email'])

  const {
    modalState: recordModalState,
    selectedData: recordSelectedData,
    toggleModal: toggleRecordModal,
    closeModel: closeRecordModel
  } = useModalManager<
    'change-priority' | 'change-status' | 'view-evaluation' | 'view-summary',
    StaffApplication
  >(['change-priority', 'change-status', 'view-evaluation', 'view-summary'])

  const { data: currentScoreCard, isPending: isCurrentScoreCardPending } = useQuery({
    queryFn: () => {
      if (recordSelectedData) return getStaffApplicationCurrentScoreCard(recordSelectedData.id)
    },
    queryKey: ['score-card', recordSelectedData?.id],
    enabled: !!(recordModalState['view-evaluation'] && recordSelectedData)
  })

  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  const [rejectApplicationWithEmailLoading, setRejectApplicationWithEmailLoading] = useState(false)

  const [rejectApplicationLoading, setRejectApplicationLoading] = useState(false)

  const { mutateAsync: assignToPcrMutation } = useMutation({
    mutationFn: assignToPcr,
    onError: showAxiosError
  })

  const { mutateAsync: changeApplicationPriorityMutation } = useMutation({
    mutationFn: changeStaffApplicationPriority,
    onError: showAxiosError
  })

  const { mutateAsync: sendEmailToStaffsMutation } = useMutation({
    mutationFn: sendEmailToStaffs,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const { mutateAsync: changeApplicationStatusMutation } = useMutation({
    mutationFn: changeStaffApplicationStatus,
    onError: showAxiosError
  })

  const { mutateAsync: rejectApplicationMutation } = useMutation({
    mutationFn: rejectStaffApplication,
    onError: showAxiosError
  })

  const { mutateAsync: validateStaffApplicationsMutation } = useMutation({
    mutationFn: validateStaffApplications,
    onError: showAxiosError
  })

  const { mutateAsync: changeApplicationEvaluationMutation } = useMutation({
    mutationFn: changeStaffApplicationEvaluation,
    onError: showAxiosError,
    onSuccess: () => tableRef.current?.refetch()
  })

  const columns: Column<StaffApplication>[] = [
    {
      accessorKey: 'automaticEvaluation',
      header: 'Eval pré-entretien',
      enableSorting: true,
      cell: (data) => (
        <FacesEvaluation
          variant={mapEvaluationFace[data.row.original.automaticEvaluation]}
          onClick={(facet) => {
            changeApplicationEvaluationMutation({
              id: data.row.original.id,
              evaluation: mapFaceEvaluation[facet].value
            })
          }}
        />
      )
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      enableSorting: true,
      cell: (data) => <TableDate date={data.row.original.createdAt} />
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      enableSorting: true,
      cell: (data) => (
        <Badge
          onClick={() => toggleRecordModal('change-status', data.row.original)}
          text={staffApplicationStatusEnumMapping[data.row.original.status].label}
          variant={staffApplicationStatusEnumMapping[data.row.original.status].variant}
        />
      )
    },
    {
      accessorKey: 'priority',
      header: 'Priorité',
      enableSorting: true,
      cell: (data) => (
        <Badge
          onClick={() => {
            toggleRecordModal('change-priority', data.row.original)
          }}
          text={priorityEnumMapping[data.row.original.priority].label}
          variant={priorityEnumMapping[data.row.original.priority].variant}
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Poste',
      enableSorting: true,
      cell: (data) => (
        <div className="flex flex-wrap w-10 text-pretty">
          {staffApplicationTypeMapping[data.row.original.type].label}
        </div>
      )
    },
    {
      accessorKey: 'lastName',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => data.row.original.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      enableSorting: true,
      cell: (data) => StringFormatter.capitalize(data.row.original.firstName)
    },
    {
      accessorKey: 'gender',
      header: 'Genre',
      enableSorting: true,
      cell: (data) => (data.row.original.gender === GenderEnum.FEMALE ? 'F' : 'M')
    },
    {
      accessorKey: 'nationality',
      header: 'Nationalité',
      enableSorting: true,
      cell: (data) => getNationalityLabel(data.row.original.nationality, nationalitiesList)
    },
    {
      accessorKey: 'birthDate',
      header: 'Age',
      enableSorting: true,
      sortDescFirst: true,
      invertSorting: true,
      cell: (data) => DateFormatter.calculateAge(new Date(data.row.original.birthDate))
    },
    {
      accessorKey: 'departmentNumber',
      header: 'Dept',
      enableSorting: true,
      cell: (data) => data.row.original.departmentNumber ?? '-'
    },
    {
      accessorKey: 'partOfTheEuropeanUnion',
      header: 'Membre EN',
      enableSorting: true,
      cell: (data) => <TableBoolean value={data.row.original.partOfTheEuropeanUnion} />
    },
    {
      accessorKey: 'languagesTaught',
      header: 'Langues',
      cell: (data) => (
        <div className="grid grid-cols-2 gap-1 items-center">
          {data.row.original.languagesTaught.map((lang) => {
            if (lang.level !== LanguageLevelEnum.NOT_PRACTICED) {
              const Icon = languageMapping[lang.language].icon
              return <Icon key={lang.id} className={'size-6 rounded-full'} />
            }
          })}
        </div>
      )
    },
    {
      accessorKey: 'periods',
      header: 'Période',
      cell: ({ row }) => {
        if (row.original.periods.length === 0) return '-'
        return (
          <div className={'flex flex-wrap gap-1 py-2 w-44'}>
            {row.original.periods.map((period) => (
              <Badge key={period.id} text={period.name} variant={'warning'} />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'pcr.firstName',
      header: 'PCR attribué',
      enableSorting: true,
      cell: (data) =>
        data.row.original.pcrId ? (
          <TableLink
            label={StringFormatter.capitalize(data.row.original.pcr.firstName)}
            to={Paths.PCR_UPDATE_PAGE.replace(':id', data.row.original.pcrId.toString())}
          ></TableLink>
        ) : (
          '-'
        )
    },

    {
      accessorKey: 'id',
      header: 'Évaluation du PCR',
      cell: (data) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            toggleRecordModal('view-evaluation', data.row.original)
          }}
          label="Voir"
          icon={PlusIcon}
          disabled={!data.row.original.currentScoreCard}
        />
      )
    },
    {
      accessorKey: 'currentScoreCard.isApplicantRecommendedForHire',
      header: 'Retour du PCR',
      enableSorting: true,
      cell: (data) => (
        <Badge
          text={
            pcrResult(
              data.row.original.currentScoreCard?.isApplicantRecommendedForHire,
              data.row.original.pcrId
            ).label
          }
          variant={
            pcrResult(data.row.original.currentScoreCard?.isApplicantRecommendedForHire).variant
          }
        />
      )
    },
    {
      accessorKey: 'id',
      header: 'Consulter',
      cell: (data) => (
        <div
          className="bg-primary-light rounded-full w-fit p-1 cursor-pointer"
          onMouseOver={() => {
            toggleRecordModal('view-summary', data.row.original)
          }}
        >
          <EyeOpenIcon width="22px" height="22px" color={tailwindColors.primary.DEFAULT} />
        </div>
      )
    }
  ]

  const similarApplicationColumns: ExpandedColumn<StaffApplication, StaffApplication>[] = [
    {
      accessorKey: 'automaticEvaluation',
      display: (a) => (
        <FacesEvaluation
          variant={mapEvaluationFace[a.automaticEvaluation]}
          onClick={(facet) => {
            changeApplicationEvaluationMutation({
              id: a.id,
              evaluation: mapFaceEvaluation[facet].value
            })
          }}
        />
      )
    },
    {
      accessorKey: 'createdAt',
      display: (a) => <TableDate date={a.createdAt} />
    },
    {
      accessorKey: 'status',
      display: (a) => (
        <Badge
          onClick={() => toggleRecordModal('change-status', a)}
          text={staffApplicationStatusEnumMapping[a.status].label}
          variant={staffApplicationStatusEnumMapping[a.status].variant}
        />
      )
    },
    {
      accessorKey: 'priority',
      display: (a) => (
        <Badge
          onClick={() => {
            toggleRecordModal('change-priority', a)
          }}
          text={priorityEnumMapping[a.priority].label}
          variant={priorityEnumMapping[a.priority].variant}
        />
      )
    },
    {
      accessorKey: 'type',
      display: (a) => (
        <div className="flex flex-wrap w-10 text-pretty">
          {staffApplicationTypeMapping[a.type].label}
        </div>
      )
    },
    {
      accessorKey: 'lastName',
      display: (a) => a.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      display: (a) => StringFormatter.capitalize(a.firstName)
    },
    {
      accessorKey: 'gender',
      display: (a) => (a.gender === GenderEnum.FEMALE ? 'F' : 'M')
    },
    {
      accessorKey: 'nationality',
      display: (a) => getNationalityLabel(a.nationality, nationalitiesList)
    },
    {
      accessorKey: 'birthDate',
      display: (a) => DateFormatter.calculateAge(new Date(a.birthDate))
    },
    {
      accessorKey: 'departmentNumber',
      display: (a) => a.departmentNumber ?? '-'
    },
    {
      accessorKey: 'partOfTheEuropeanUnion',
      display: (a) => <TableBoolean value={a.partOfTheEuropeanUnion} />
    },
    {
      accessorKey: 'languagesTaught',
      display: (a) => (
        <div className="flex w-16 flex-wrap gap-1">
          {a.languagesTaught.map((lang) => {
            const Icon = languageMapping[lang.language].icon
            return <Icon key={lang.id} className={'w-4 h-4 rounded-full mb-1'} />
          })}
        </div>
      )
    },
    {
      accessorKey: 'currentDiploma',
      display: (a) => a.currentDiploma
    },
    {
      accessorKey: 'pcr.firstName',
      display: (a) =>
        a.pcrId ? (
          <TableLink
            label={StringFormatter.capitalize(a.pcr.firstName)}
            to={Paths.PCR_UPDATE_PAGE.replace(':id', a.pcrId.toString())}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'id',
      display: (a) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            toggleRecordModal('view-evaluation', a)
          }}
          label="Voir"
          icon={PlusIcon}
          size="small"
          disabled={!a.currentScoreCard}
        />
      )
    },
    {
      accessorKey: 'currentScoreCard.isApplicantRecommendedForHire',
      display: (a) => (
        <Badge
          text={pcrResult(a.currentScoreCard?.isApplicantRecommendedForHire).label}
          variant={pcrResult(a.currentScoreCard?.isApplicantRecommendedForHire).variant}
        />
      )
    },
    {
      accessorKey: 'id',
      display: (a) => (
        <div
          className="bg-primary-light rounded-full w-fit p-1 cursor-pointer"
          onMouseOver={() => toggleRecordModal('view-summary', a)}
        >
          <EyeOpenIcon width="22px" height="22px" color={tailwindColors.primary.DEFAULT} />
        </div>
      )
    }
  ]

  const emails =
    selectedData
      ?.filter((row, index, self) => index === self.findIndex((t) => t.email === row.email))
      .map((row) => row.email) ?? []

  return (
    <div>
      <UserNavBar title="Candidatures" icon={NotebookIcon} />
      <div className="h-4" />
      <DynamicDataTable<StaffApplication, StaffApplicationFilters, StaffApplication>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Candidatures encadrants reçues"
        columns={columns}
        onFiltersClick={() => toggleModal('filters')}
        permissions={{ delete: false }}
        allowSearchParam
        version="FULL"
        selectable
        endpoint={API.candidate.getAll.url}
        expandedOptions={{
          key: 'similarApplications',
          alwaysOpen: true,
          columns: similarApplicationColumns
        }}
        onRowClick={(row, event) => {
          navigateWithEvent(
            Paths.UPDATE_STAFF_APPLICATION_PAGE.replace(':id', row.id.toString()),
            event
          )
        }}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['similarApplications'])}
        actions={[
          {
            type: 'multiple',
            icon: PlusIcon,
            label: 'Assigner au PCR',
            fn: (applications) => {
              toggleModal('assign-pcr', applications)
            }
          },
          {
            type: 'multiple',
            icon: UserCheckIcon,
            label: 'Valider',
            variant: 'ghost',
            requiredConfirmation: true,
            refetch: true,
            confirmText: 'Accepter',
            cancelText: 'Fermer',
            confirmationMessage: 'Êtes-vous sûr de vouloir valider cette candidature ?',
            fn: async (applications) => {
              const validatedApplications = await validateStaffApplicationsMutation({
                ids: applications.map((a) => a.id)
              })

              if (applications.length === 1 && validatedApplications[0]?.staffId)
                navigate(
                  Paths.STAFF_UPDATE_PAGE.replace(
                    ':id',
                    validatedApplications[0].staffId.toString()
                  )
                )
            }
          },
          {
            type: 'multiple',
            icon: UserCanceledIcon,
            label: 'Rejeter',
            variant: 'ghost',
            fn: (applications) => {
              toggleModal('reject-application', applications)
            }
          },
          {
            type: 'multiple',
            icon: UserCheckIcon,
            label: 'Stand by',
            variant: 'ghost',
            requiredConfirmation: true,
            refetch: true,
            cancelText: 'Fermer',
            confirmationMessage: 'Êtes-vous sûr de vouloir mettre en stand by cette candidature ?',
            fn: async (applications) => {
              await Promise.all(
                applications.map((application) =>
                  changeApplicationStatusMutation({
                    id: application.id,
                    status: StaffApplicationStatusEnum.STAND_BY
                  })
                )
              )
            }
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Envoyer un rappel au PCR',
            variant: 'ghost',
            successMessage: 'Rappel envoyé avec succès',
            fn: async (applications) => {
              const ids = applications.map((a) => a.id)
              await sendReminderToPcr({ ids })
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Envoyer un email',
            variant: 'ghost',
            fn: (applications) => {
              toggleModal('send-email', applications)
            }
          }
        ]}
      />

      {/* Assign to Pcr */}
      <Modal
        isOpen={modalState['assign-pcr']}
        title="Assigner au PCR"
        icon={() => (
          <UserIcon
            fill={tailwindColors.secondary.light.DEFAULT}
            color={tailwindColors.secondary.DEFAULT}
          />
        )}
        hideActions
        onCancel={() => closeModel('assign-pcr')}
      >
        <AssignPcrForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            priority:
              selectedData && selectedData?.length > 1
                ? PriorityEnum.NORMAL
                : selectedData?.[0]?.priority,
            pcr: selectedData?.[0]?.pcr
          }}
          buttonsConfig={modalButtonConfig({
            confirmText: selectedData && selectedData?.[0]?.pcr ? 'Mettre à jour' : 'Assigner',
            onCancel: () => closeModel('assign-pcr'),
            onConfirm: async (values) => {
              if (!selectedData) return
              else await assignToPcrMutation({ ids: selectedData.map((s) => s.id), ...values })
              tableRef.current?.refetch()
              toggleModal('assign-pcr')
            }
          })}
        />
      </Modal>

      {/* Reject Application */}
      <Modal
        isOpen={modalState['reject-application']}
        title="Etes-vous sûr de vouloir rejeter cette candidature?"
        size="medium"
        icon={XIcon}
        variant="destructive"
        hideActions
        onCancel={() => closeModel('reject-application')}
        customActions={
          <div className="flex gap-4 justify-center">
            <Button
              label="Annuler"
              variant="ghost"
              onClick={() => closeModel('reject-application')}
            />
            <Button
              label="Rejeter en envoyant une réponse"
              onClick={async () => {
                if (!selectedData) return
                setRejectApplicationWithEmailLoading(true)
                await rejectApplicationMutation({
                  ids: selectedData.map((a) => a.id),
                  sendEmail: true
                })
                setRejectApplicationWithEmailLoading(false)
                tableRef.current?.refetch()
                closeModel('reject-application')
              }}
              loading={rejectApplicationWithEmailLoading}
            />
            <Button
              label="Rejeter sans envoyer de réponse"
              onClick={async () => {
                if (!selectedData) return
                setRejectApplicationLoading(true)
                await rejectApplicationMutation({
                  ids: selectedData.map((a) => a.id),
                  sendEmail: false
                })
                setRejectApplicationLoading(false)
                tableRef.current?.refetch()
                toggleModal('reject-application')
              }}
              loading={rejectApplicationLoading}
            />
          </div>
        }
      />

      {/* Change Application Priority */}
      <Modal
        isOpen={recordModalState['change-priority']}
        title="Priorité"
        icon={LevelIcon}
        hideActions
        onCancel={() => toggleRecordModal('change-priority')}
      >
        <StaffApplicationPriorityForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={
            recordSelectedData?.priority ? { priority: recordSelectedData?.priority } : undefined
          }
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleRecordModal('change-priority'),
            onConfirm: async (values) => {
              if (!recordSelectedData) return
              await changeApplicationPriorityMutation({
                id: recordSelectedData.id,
                ...values
              })
              tableRef.current?.refetch()
              toggleRecordModal('change-priority')
            }
          })}
        />
      </Modal>

      {/* View Application Summary */}
      <Modal
        isOpen={recordModalState['view-summary']}
        size="large"
        hideActions
        hideHeader
        onCancel={() => toggleRecordModal('view-summary')}
      >
        {recordSelectedData && (
          <div className={MODAL_BODY_CLASSNAME}>
            <StaffApplicationSummary staffApplication={recordSelectedData} />
            <div className="h-4" />
          </div>
        )}
      </Modal>

      {/* Change Application Status */}
      <Modal
        isOpen={recordModalState['change-status']}
        title="Statut"
        icon={ListApprovedIcon}
        hideActions
        onCancel={() => toggleRecordModal('change-status')}
      >
        <StaffApplicationStatusForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={
            recordSelectedData?.status ? { status: recordSelectedData?.status } : undefined
          }
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleRecordModal('change-status'),
            onConfirm: async (values) => {
              if (!recordSelectedData) return
              await changeApplicationStatusMutation({ id: recordSelectedData.id, ...values })
              tableRef.current?.refetch()
              toggleRecordModal('change-status')
            }
          })}
        />
      </Modal>

      {/* View Application Evaluation */}
      {recordSelectedData && currentScoreCard && (
        <PcrScoreCardModal
          status={recordSelectedData.status}
          readOnly
          defaultTab={2}
          modalLoading={isCurrentScoreCardPending}
          isOpen={recordModalState['view-evaluation']}
          onCancel={() => closeRecordModel('view-evaluation')}
          staffApplication={recordSelectedData}
          defaultValues={mapScoreCardToInput(currentScoreCard)}
          handleReset={() => closeRecordModel('view-evaluation')}
        />
      )}
      {/* Send email to candidate */}
      <Modal
        isOpen={modalState['send-email']}
        title={'Envoyer un email'}
        icon={EmailIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('send-email')}
      >
        {selectedData && (
          <SendEmailForm
            className="modal-body"
            defaultValues={{
              emails,
              subject: '',
              body: ''
            }}
            labels={selectedData.map(
              (s) =>
                `${StringFormatter.capitalize(s.firstName)} ${s.lastName.toUpperCase()} - ${s.email}`
            )}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              onConfirm: async (values) =>
                await sendEmailToStaffsMutation({
                  ...values,
                  emails
                })
            })}
          />
        )}
      </Modal>

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={() => onSelectedFilterChange({ ...applicationsEmptyFilter })}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffApplicationsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default StaffApplicationsList
